import React, { useEffect, useState } from "react";

import { AttributeType } from "@energy-advice/easas-api";
import { useGetProductTemplateConfig } from "api/api-hooks/product-templates-hooks";
import { ProductTemplateDropdown } from "components/basic/dropdowns/product-template/ProductTemplateDropdown";
import { DashboardEditorProps } from "components/ComponentTypeTypes";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";
import { MultiSelect } from "primereact/multiselect";
import { ProductItemMapProps } from "../types";

export const DEFAULT_HEIGHT = 500;

const ProductItemMapEditor: React.FC<DashboardEditorProps<ProductItemMapProps>> = ({
  config,
  onConfigChange,
}) => {
  const [currentConfig, setCurrentConfig] = useState<ProductItemMapProps>(() => config);

  const { data } = useGetProductTemplateConfig(
    currentConfig.safeName,
    `${currentConfig.productTemplateId}`,
    [],
    [],
    {
      enabled: !!currentConfig.safeName && !Number.isNaN(Number(currentConfig.productTemplateId)),
    }
  );

  useEffect(() => {
    onConfigChange(currentConfig);
  }, [currentConfig]);

  const handleChange = (field: keyof ProductItemMapProps, value: string | number | null) => {
    setCurrentConfig(prev => ({ ...prev, [field]: value }))
  }

  const doubleColumns = data?.data?.attributes.filter(attr => attr.type === AttributeType.DOUBLE) ?? []

  return (
    <div className="grid p-fluid gap-3">
      <div className="flex flex-wrap w-full gap-3">
        <div className="w-full sm:w-3 gap-2 flex flex-column justify-content-center">
          <label htmlFor="height">{T("word.height")}</label>
          <InputNumber
            value={currentConfig.height ?? DEFAULT_HEIGHT}
            id="height"
            suffix=" px"
            className="p-inputtext-sm w-14rem"
            onChange={e => handleChange("height", e.value)}
          />
        </div>
        <div className="w-full sm:w-3 gap-2 flex flex-column justify-content-center">
          <label htmlFor="filter">{T("word.filter")}</label>
          <InputText
            value={currentConfig.filter ?? ""}
            id="filter"
            className="p-inputtext-sm w-14rem"
            onChange={e => handleChange("filter", e.target.value)}
          />
        </div>
        <div className="w-full sm:w-3 gap-2 flex flex-column justify-content-center">
          <label htmlFor="hiddenColumns">{T("component.hidden-columns")}</label>
          <MultiSelect
            value={currentConfig.hiddenColumns ?? ""}
            options={data?.data?.attributes ?? []}
            optionLabel="displayName"
            optionValue="referenceName"
            placeholder={T("action.select-column")}
            maxSelectedLabels={1}
            id="hiddenColumns"
            className="p-inputtext-sm w-14rem"
            onChange={e => handleChange("hiddenColumns", e.target.value)}
          />
        </div>
      </div>
      <div className="flex flex-row w-full">
        <div className="w-full flex flex-column gap-2">
          <label htmlFor="safeName">{T("word.safe-name")}</label>
          <ProductTemplateDropdown
            value={currentConfig.productTemplateId}
            onChange={e =>
              setCurrentConfig(prevState => ({
                ...prevState,
                safeName: e.refName,
                productTemplateId: e.value,
              }))
            }
          />
        </div>
      </div>
      <div className="flex flex-wrap w-full gap-3">
        <div className="w-full sm:w-3 gap-2 flex flex-column justify-content-center">
          <label htmlFor="latitude">{T("word.latitude")}</label>
          <Dropdown
            value={currentConfig.latitude ?? ""}
            options={doubleColumns}
            optionLabel="displayName"
            optionValue="referenceName"
            placeholder={T("action.select-column")}
            showClear
            id="latitude"
            className="p-inputtext-sm w-14rem"
            onChange={e => handleChange("latitude", e.target.value)}
          />
        </div>
        <div className="w-full sm:w-3 gap-2 flex flex-column justify-content-center">
          <label htmlFor="longitude">{T("word.longitude")}</label>
          <Dropdown
            value={currentConfig.longitude ?? ""}
            options={doubleColumns}
            optionLabel="displayName"
            optionValue="referenceName"
            placeholder={T("action.select-column")}
            showClear
            id="longitude"
            className="p-inputtext-sm w-14rem"
            onChange={e => handleChange("longitude", e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductItemMapEditor;
